import { NavLink } from 'react-router-dom';
import type { AccentTone } from '@/app/dashboard/navigation';
import { accentBarClass } from '@/app/dashboard/accent';

interface RouteTab {
  to: string;
  label: string;
  end?: boolean;
}

interface RouteTabsProps {
  tabs: RouteTab[];
  /** Matches docs/DESIGN_SYSTEM.md's section-to-accent mapping for whichever page renders this. */
  accent?: AccentTone;
}

/**
 * Same visual language as Tabs (docs/DESIGN_SYSTEM.md §4.7) but backed by
 * real routes via NavLink instead of local state — used for dashboard
 * sub-navigation (e.g. Community's 7 sub-features, Owner Console's 3
 * sections) so each sub-page has its own shareable, back-button-friendly
 * URL. Accent defaults to 'community' since that's this component's first
 * caller, but every usage should pass the accent matching its own section.
 */
export function RouteTabs({ tabs, accent = 'community' }: RouteTabsProps) {
  const activeBorder = accentBarClass[accent];
  return (
    <div className="flex gap-6 border-b border-border-subtle overflow-x-auto" role="tablist">
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          className={({ isActive }) =>
            [
              'pb-3 -mb-px text-body-medium border-b-2 transition-colors min-h-[44px] whitespace-nowrap',
              isActive ? `${activeBorder} text-text-primary` : 'border-transparent text-text-secondary hover:text-text-primary',
            ].join(' ')
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </div>
  );
}
